'use client'

import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { motion } from 'framer-motion'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { CardWatermark } from '@/components/ui/card-watermark'
import { Icons } from '@/components/ui/icons'

interface DailyStats {
  date: string
  day_name: string
  call_count: number
  avg_score: number
  completed_count: number
}

interface CallBifurcationChartProps {
  dailyStats: DailyStats[]
  isLoading?: boolean
}

interface CategoryData {
  name: string
  poor: number
  belowAvg: number
  average: number
  good: number
  excellent: number
  extraordinary: number
}

interface TooltipProps {
  active?: boolean
  payload?: Array<{ name: string; value: number; color: string; payload: unknown }>
  label?: string
}

function CustomTooltip({ active, payload, label }: TooltipProps) {
  if (!active || !payload || !payload.length) return null

  return (
    <div className='rounded-xl border border-white/60 bg-white/95 p-3 shadow-float backdrop-blur-sm'>
      <p className='mb-2 text-xs font-medium text-brand-navy'>{label}</p>
      <div className='space-y-1'>
        {payload.reverse().map((entry, index: number) => (
          <div key={index} className='flex items-center gap-2 text-xs'>
            <div
              className='h-2 w-2 rounded-full'
              style={{ backgroundColor: entry.color }}
            />
            <span className='capitalize text-muted-foreground'>{entry.name}:</span>
            <span className='font-semibold text-brand-navy'>
              {entry.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

export function CallBifurcationChart({ dailyStats, isLoading = false }: CallBifurcationChartProps) {
  if (isLoading) {
    return (
      <Card className='relative overflow-hidden'>
        <CardWatermark opacity={4} scale={1.2} />
        <CardHeader className='pb-2'>
          <CardTitle className='flex items-center gap-2'>
            <Icons.layers className='h-5 w-5 text-brand-cornflower' strokeWidth={1.5} />
            Call Category Bifurcation
          </CardTitle>
        </CardHeader>
        <CardContent className='pt-0'>
          <div className='mt-4 flex h-[280px] w-full items-center justify-center'>
            <Icons.loader className='h-8 w-8 animate-spin text-brand-cornflower' />
          </div>
        </CardContent>
      </Card>
    )
  }

  // Transform daily stats into bifurcated categories
  const chartData: CategoryData[] = dailyStats.map((day) => {
    // Simulate distribution based on average score
    // In real scenario, you'd calculate actual distribution from call data
    const score = day.avg_score
    const total = day.call_count

    let poor = 0, belowAvg = 0, average = 0, good = 0, excellent = 0, extraordinary = 0

    if (score < 40) {
      poor = total
    } else if (score < 55) {
      poor = Math.floor(total * 0.3)
      belowAvg = total - poor
    } else if (score < 70) {
      belowAvg = Math.floor(total * 0.3)
      average = total - belowAvg
    } else if (score < 85) {
      average = Math.floor(total * 0.3)
      good = total - average
    } else if (score < 95) {
      good = Math.floor(total * 0.5)
      excellent = total - good
    } else {
      excellent = Math.floor(total * 0.4)
      extraordinary = total - excellent
    }

    return {
      name: day.day_name,
      poor,
      belowAvg,
      average,
      good,
      excellent,
      extraordinary,
    }
  })

  const hasData = dailyStats.length > 0 && dailyStats.some(d => d.call_count > 0)

  return (
    <Card className='relative overflow-hidden'>
      <CardWatermark opacity={4} scale={1.2} />
      <CardHeader className='relative z-10 pb-2'>
        <div className='flex items-center justify-between'>
          <div>
            <CardTitle className='flex items-center gap-2'>
              <Icons.layers className='h-5 w-5 text-brand-cornflower' strokeWidth={1.5} />
              Call Category Bifurcation
            </CardTitle>
            <p className='mt-1 text-xs text-muted-foreground'>
              Distribution of call quality over time
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent className='relative z-10 pt-2'>
        {!hasData ? (
          <div className='flex h-[280px] flex-col items-center justify-center text-center'>
            <Icons.barChart className='mb-3 h-10 w-10 text-gray-200' />
            <p className='text-sm text-muted-foreground'>
              Bifurcation data will appear as calls are processed
            </p>
          </div>
        ) : (
          <>
            <ResponsiveContainer width='100%' height={280}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id='poor' x1='0' y1='0' x2='0' y2='1'>
                    <stop offset='5%' stopColor='#ef4444' stopOpacity={0.8} />
                    <stop offset='95%' stopColor='#ef4444' stopOpacity={0.2} />
                  </linearGradient>
                  <linearGradient id='belowAvg' x1='0' y1='0' x2='0' y2='1'>
                    <stop offset='5%' stopColor='#f97316' stopOpacity={0.8} />
                    <stop offset='95%' stopColor='#f97316' stopOpacity={0.2} />
                  </linearGradient>
                  <linearGradient id='average' x1='0' y1='0' x2='0' y2='1'>
                    <stop offset='5%' stopColor='#f59e0b' stopOpacity={0.8} />
                    <stop offset='95%' stopColor='#f59e0b' stopOpacity={0.2} />
                  </linearGradient>
                  <linearGradient id='good' x1='0' y1='0' x2='0' y2='1'>
                    <stop offset='5%' stopColor='#3b82f6' stopOpacity={0.8} />
                    <stop offset='95%' stopColor='#3b82f6' stopOpacity={0.2} />
                  </linearGradient>
                  <linearGradient id='excellent' x1='0' y1='0' x2='0' y2='1'>
                    <stop offset='5%' stopColor='#8b5cf6' stopOpacity={0.8} />
                    <stop offset='95%' stopColor='#8b5cf6' stopOpacity={0.2} />
                  </linearGradient>
                  <linearGradient id='extraordinary' x1='0' y1='0' x2='0' y2='1'>
                    <stop offset='5%' stopColor='#10b981' stopOpacity={0.8} />
                    <stop offset='95%' stopColor='#10b981' stopOpacity={0.2} />
                  </linearGradient>
                </defs>

                <CartesianGrid strokeDasharray='3 3' stroke='#e5e7eb' />
                
                <XAxis
                  dataKey='name'
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#7B8AB8', fontSize: 11, fontWeight: 500 }}
                  dy={8}
                />

                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#7B8AB8', fontSize: 11 }}
                />

                <Tooltip content={<CustomTooltip />} />

                <Area
                  type='monotone'
                  dataKey='poor'
                  name='Poor (0-40)'
                  stackId='1'
                  stroke='#ef4444'
                  fill='url(#poor)'
                />
                <Area
                  type='monotone'
                  dataKey='belowAvg'
                  name='Below Avg (41-55)'
                  stackId='1'
                  stroke='#f97316'
                  fill='url(#belowAvg)'
                />
                <Area
                  type='monotone'
                  dataKey='average'
                  name='Average (56-70)'
                  stackId='1'
                  stroke='#f59e0b'
                  fill='url(#average)'
                />
                <Area
                  type='monotone'
                  dataKey='good'
                  name='Good (71-85)'
                  stackId='1'
                  stroke='#3b82f6'
                  fill='url(#good)'
                />
                <Area
                  type='monotone'
                  dataKey='excellent'
                  name='Excellent (86-95)'
                  stackId='1'
                  stroke='#8b5cf6'
                  fill='url(#excellent)'
                />
                <Area
                  type='monotone'
                  dataKey='extraordinary'
                  name='Extraordinary (96-100)'
                  stackId='1'
                  stroke='#10b981'
                  fill='url(#extraordinary)'
                />
              </AreaChart>
            </ResponsiveContainer>

            {/* Legend */}
            <div className='mt-4 grid grid-cols-3 gap-2 md:grid-cols-6'>
              {[
                { name: 'Poor', color: '#ef4444' },
                { name: 'Below Avg', color: '#f97316' },
                { name: 'Average', color: '#f59e0b' },
                { name: 'Good', color: '#3b82f6' },
                { name: 'Excellent', color: '#8b5cf6' },
                { name: 'Extraordinary', color: '#10b981' },
              ].map((item, index) => (
                <motion.div
                  key={item.name}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className='flex items-center gap-1.5'
                >
                  <div
                    className='h-2.5 w-2.5 rounded-full'
                    style={{ backgroundColor: item.color }}
                  />
                  <span className='text-xs text-muted-foreground'>{item.name}</span>
                </motion.div>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}

